'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  Check,
  Edit2,
  FileText,
  Link as LinkIcon,
  Loader2,
  Network,
  Play,
  Send,
  Undo2,
  User,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@clerk/clerk-react';
import { useMutation } from '@tanstack/react-query';
import { createApiClient } from '@/lib/api-client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  useProcedure,
  useUpdateProcedure,
  usePublishProcedure,
  useUnpublishProcedure,
} from '@/features/policies/api/use-procedures';
import { usePolicies } from '@/features/policies/api/use-policies';
import {
  useProcedureDocumentLinks,
  useUnlinkProcedureDocument,
} from '@/features/policies/api/use-procedure-document-links';
import { ProcedureDocumentLinkDialog } from '@/features/policies/components/ProcedureDocumentLinkDialog';
import { useMembers } from '@/features/team/api/use-members';
import { ProcedureFlowBuilder } from '@/features/procedures/components/ProcedureFlowBuilder';
import { normalizeProcedureFlow } from '@/features/procedures/lib/flow-builder';
import { ProcedureStatusBadge } from '@/features/procedures/components/ProcedureStatusBadge';
import type { Procedure, ProcedureFlow } from '@/features/policies/types';
import type { MembersListResponse } from '@/features/team/types';
import { formatDate } from '@/lib/format';
import type { TranslateFn } from '@/lib/i18n';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/procedures/$procedureId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.procedureDetails', 'Procedure Details'),
  }),
  component: ProcedureDetailPage,
});

function getOwnerName(owner: {
  firstName: string | null;
  lastName: string | null;
  email: string;
}): string {
  if (owner.firstName && owner.lastName) {
    return `${owner.firstName} ${owner.lastName}`;
  }
  return owner.email;
}

function getRecurrenceLabel(recurrence: Procedure['recurrence'], t: TranslateFn) {
  switch (recurrence) {
    case 'one_time':
      return t('policies:recurrence.one_time');
    case 'daily':
      return t('policies:recurrence.daily');
    case 'weekly':
      return t('policies:recurrence.weekly');
    case 'monthly':
      return t('policies:recurrence.monthly');
    case 'quarterly':
      return t('policies:recurrence.quarterly');
    case 'annually':
      return t('policies:recurrence.annually');
    default:
      return recurrence;
  }
}

function ProcedureDetailPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { procedureId } = Route.useParams();
  const { organization } = useOrganization();
  const orgId = (organization?.publicMetadata as { internalId?: string } | null)?.internalId;

  const { getToken } = useAuth();
  const { procedure, isLoading, isError, refetch } = useProcedure({ procedureId });
  const updateMutation = useUpdateProcedure(procedureId);
  const publishMutation = usePublishProcedure(procedureId);
  const unpublishMutation = useUnpublishProcedure(procedureId);

  const executeMutation = useMutation({
    mutationFn: async () => {
      const apiClient = createApiClient({ getAuthToken: getToken });
      return apiClient.post(`/procedures/${procedureId}/instances`, { notes: null });
    },
    onSuccess: () => {
      toast.success(t('procedures.executionStarted'));
      void refetch();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('procedures.executionStartFailed'));
    },
  });
  const { documentLinks, total: documentLinksTotal } = useProcedureDocumentLinks({ procedureId });
  const unlinkDocumentMutation = useUnlinkProcedureDocument();

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editRecurrence, setEditRecurrence] = useState('one_time');
  const [editOwnerId, setEditOwnerId] = useState<string | null>(null);
  const [editSourcePolicyId, setEditSourcePolicyId] = useState<string | null>(null);
  const [editFlow, setEditFlow] = useState<ProcedureFlow | null>(null);

  // Data for dropdowns (only fetch when editing)
  const policiesQuery = usePolicies({
    filters: {
      status: ['published', 'draft'],
      sortBy: 'title',
      sortOrder: 'asc',
      categoryId: null,
      frameworkId: null,
      type: null,
      search: null,
    },
    enabled: isEditing,
  });
  const membersQuery = useMembers({
    orgId: orgId || '',
    pageSize: 100,
    enabled: isEditing && !!orgId,
  });

  const handleBack = () => {
    void navigate({ to: '/procedures' });
  };

  const startEditing = () => {
    if (!procedure) return;
    setEditTitle(procedure.title);
    setEditDescription(procedure.description || '');
    setEditRecurrence(procedure.recurrence);
    setEditOwnerId(procedure.ownerId);
    setEditSourcePolicyId(procedure.sourcePolicyId);
    setEditFlow(normalizeProcedureFlow(procedure.flow, procedure.steps));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setEditFlow(null);
    setIsEditing(false);
  };

  const saveChanges = () => {
    updateMutation.mutate(
      {
        title: editTitle,
        description: editDescription || null,
        recurrence: editRecurrence as Procedure['recurrence'],
        ownerId: editOwnerId,
        sourcePolicyId: editSourcePolicyId,
        flow: editFlow,
      },
      {
        onSuccess: () => {
          toast.success(t('procedures.updated'));
          setEditFlow(null);
          setIsEditing(false);
          void refetch();
        },
        onError: (error) => {
          toast.error(error.message || t('procedures.updateFailed'));
        },
      }
    );
  };

  const handlePublish = () => {
    publishMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('procedures.published'));
        void refetch();
      },
      onError: (error) => {
        toast.error(error.message || t('procedures.publishFailed'));
      },
    });
  };

  const handleUnpublish = () => {
    unpublishMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('procedures.revertedToDraft'));
        void refetch();
      },
      onError: (error) => {
        toast.error(error.message || t('procedures.revertFailed'));
      },
    });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-[1680px] space-y-6">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !procedure) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-[1680px] space-y-6">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('procedures.backToProcedures')}
          </Button>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">{t('procedures.notFoundOrNoAccess')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const isDraft = procedure.status === 'draft';
  const isActive = procedure.status === 'active';
  const recurrenceLabel = getRecurrenceLabel(procedure.recurrence, t);
  const recurrenceOptions = [
    { value: 'one_time', label: t('policies:recurrence.one_time') },
    { value: 'daily', label: t('policies:recurrence.daily') },
    { value: 'weekly', label: t('policies:recurrence.weekly') },
    { value: 'monthly', label: t('policies:recurrence.monthly') },
    { value: 'quarterly', label: t('policies:recurrence.quarterly') },
    { value: 'annually', label: t('policies:recurrence.annually') },
  ];
  const effectiveFlow = isEditing
    ? normalizeProcedureFlow(editFlow, procedure.steps)
    : normalizeProcedureFlow(procedure.flow, procedure.steps);

  // Members list for owner dropdown
  const members: Array<{ id: string; name: string }> = [];
  if (membersQuery.data) {
    const data = membersQuery.data as MembersListResponse;
    if (data.members) {
      for (const m of data.members) {
        const name =
          m.firstName && m.lastName ? `${m.firstName} ${m.lastName}` : m.email || 'Unknown';
        // Use userId (FK to users table), not membership id
        members.push({ id: m.userId || m.id, name });
      }
    }
  }

  // Policies list for linking
  const policies: Array<{ id: string; title: string }> = [];
  if (policiesQuery.data?.pages) {
    for (const page of policiesQuery.data.pages) {
      for (const p of page.items) {
        policies.push({ id: p.id, title: p.title });
      }
    }
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-[1680px] space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('procedures.backToProcedures')}
          </Button>
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              {isEditing ? (
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="text-2xl font-semibold"
                  placeholder={t('procedures.procedureTitlePlaceholder')}
                />
              ) : (
                <h1 className="text-2xl font-semibold">{procedure.title}</h1>
              )}
              <div className="flex items-center gap-2">
                <ProcedureStatusBadge status={procedure.status} />
                {!isEditing && <Badge variant="secondary">{recurrenceLabel}</Badge>}
                {isActive && procedure.publishedAt && (
                  <span className="text-xs text-muted-foreground">
                    {t('procedures.publishedOn', { date: formatDate(procedure.publishedAt) })}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isDraft && !isEditing && (
                <>
                  <Button variant="outline" size="sm" className="gap-1" onClick={startEditing}>
                    <Edit2 className="h-4 w-4" />
                    {t('procedures.edit')}
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1"
                    onClick={handlePublish}
                    disabled={publishMutation.isPending}
                  >
                    {publishMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    {t('procedures.publish')}
                  </Button>
                </>
              )}
              {isDraft && isEditing && (
                <>
                  <Button variant="ghost" size="sm" className="gap-1" onClick={cancelEditing}>
                    <X className="h-4 w-4" />
                    {t('procedures.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1"
                    onClick={saveChanges}
                    disabled={updateMutation.isPending || !editTitle.trim()}
                  >
                    {updateMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    {t('procedures.save')}
                  </Button>
                </>
              )}
              {isActive && (
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      onClick={handleUnpublish}
                      disabled={unpublishMutation.isPending}
                    >
                      {unpublishMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Undo2 className="h-4 w-4" />
                      )}
                      {t('procedures.revertToDraftToEdit')}
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1"
                      onClick={() => executeMutation.mutate()}
                      disabled={executeMutation.isPending}
                    >
                      {executeMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      {t('procedures.startExecution')}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('procedures.activeReadOnlyHint')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <Card>
          <CardHeader>
            <CardTitle>{t('procedures.description')}</CardTitle>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <Textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={t('procedures.descriptionPlaceholder')}
                rows={4}
              />
            ) : procedure.description ? (
              <p className="whitespace-pre-wrap text-muted-foreground">{procedure.description}</p>
            ) : (
              <p className="text-sm italic text-muted-foreground">
                {t('procedures.noDescription')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recurrence (editable) */}
        {isEditing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t('procedures.recurrence')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={editRecurrence} onValueChange={setEditRecurrence}>
                <SelectTrigger className="max-w-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {recurrenceOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Source Policy */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <BookOpen className="h-4 w-4" />
                {t('procedures.sourcePolicy')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <Select
                  value={editSourcePolicyId || '_none'}
                  onValueChange={(v) => setEditSourcePolicyId(v === '_none' ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('procedures.selectPolicyPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">{t('common:routeErrors.noLinkedPolicy')}</SelectItem>
                    {policies.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : procedure.sourcePolicy ? (
                <Link
                  to="/policies/$policyId"
                  params={{ policyId: procedure.sourcePolicy.id }}
                  className="text-primary hover:underline"
                >
                  {procedure.sourcePolicy.title}
                </Link>
              ) : (
                <span className="text-sm italic text-muted-foreground">
                  {t('common:routeErrors.noLinkedPolicy')}
                </span>
              )}
            </CardContent>
          </Card>

          {/* Owner */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-medium">
                <User className="h-4 w-4" />
                {t('procedures.owner')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <Select value={editOwnerId || ''} onValueChange={(v) => setEditOwnerId(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('procedures.selectOwnerPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <span>{getOwnerName(procedure.owner)}</span>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Schedule Info */}
        {!isEditing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t('procedures.schedule')}
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 text-sm md:grid-cols-3">
              <div>
                <span className="text-muted-foreground">{t('procedures.recurrence')}:</span>
                <span className="ml-2">{recurrenceLabel}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t('procedures.nextScheduled')}:</span>
                <span className="ml-2">
                  {procedure.nextScheduledDate ? formatDate(procedure.nextScheduledDate) : '-'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">{t('procedures.created')}:</span>
                <span className="ml-2">{formatDate(procedure.createdAt)}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Linked Documents */}
        {!isEditing && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {t('procedures.documents', { count: documentLinksTotal })}
                </CardTitle>
                <ProcedureDocumentLinkDialog
                  procedureId={procedureId}
                  existingDocumentIds={documentLinks.map((l) => l.documentId)}
                  trigger={
                    <Button variant="outline" size="sm" className="gap-1">
                      <LinkIcon className="h-4 w-4" />
                      {t('procedures.linkDocument')}
                    </Button>
                  }
                />
              </div>
            </CardHeader>
            <CardContent>
              {documentLinks.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">
                  {t('procedures.noDocumentsLinked')}
                </p>
              ) : (
                <div className="space-y-3">
                  {documentLinks.map((link) => (
                    <div
                      key={link.id}
                      className="flex items-center justify-between rounded-lg border p-3"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{link.document.title}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{link.document.fileName}</span>
                            <Badge variant="outline" className="text-xs">
                              {link.linkType}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          unlinkDocumentMutation.mutate(
                            { procedureId, documentId: link.documentId },
                            {
                              onSuccess: () => toast.success(t('procedures.documentUnlinked')),
                              onError: () => toast.error(t('procedures.unlinkDocumentFailed')),
                            }
                          );
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Flow Builder */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Network className="h-5 w-5" />
              {t('procedures.builderTitle')}
            </CardTitle>
            <CardDescription>{t('procedures.builderDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
            <ProcedureFlowBuilder
              value={effectiveFlow}
              readOnly={!isEditing}
              onChange={isEditing ? setEditFlow : undefined}
              onRequestEdit={isDraft ? startEditing : undefined}
              members={members}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
